import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Inbox, Loader2, RefreshCw } from 'lucide-react';
import {
  fetchInboundMail, generateIntakeAddress, getDisplayErrorMessage, listReceivedMail, rereadReceivedMail, saveInboundMailSettings,
  type RereadReport,
} from '@/lib/api';
import styles from './SettingsPage.module.css';

/**
 * Inbound mail (29 October 2026, Warren's item 3, intake first): the support@
 * intake address to add to the Google Group, the capture checklist, the mail
 * a person must read (quarantine, a Google Groups invitation), Re-read
 * received mail, and the values the pipeline reads. The Api decides who sees
 * it (Super Admin, routes/admin/inbound-mail.ts): on a refusal the section
 * shows nothing. Receiving itself is switched by infra (INBOUND_EMAIL_ENABLED),
 * never here.
 */
const KEY = ['inbound-mail'];
const RECEIVED_KEY = ['inbound-mail', 'received'];
const NUMBER_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'flood_per_sender_per_day', label: 'Messages a day from one sender before processing stops' },
  { key: 'support_thread_window_days', label: "Days a support@ message joins the same sender's open thread" },
  { key: 'alias_thread_window_days', label: "Days a reply joins the driver's open thread" },
  { key: 'attachment_window_days', label: 'Days an attachment can be downloaded (the S3 copy keeps 30)' },
  { key: 'match_date_slack_days', label: 'Days either side a stated date matches' },
  { key: 'fx_band_low', label: 'CAD per USD band, low' },
  { key: 'fx_band_high', label: 'CAD per USD band, high' },
  { key: 'last4_max_stripe_reads', label: 'Card last 4 reads per message (read only, at most 10)' },
  { key: 'stripe_reads_per_day', label: 'Read-only Stripe reads a day for matching, across the network (0 turns them off)' },
];

export function InboundMailSection() {
  const client = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: fetchInboundMail, retry: false, staleTime: 30_000 });
  const received = useQuery({ queryKey: RECEIVED_KEY, queryFn: () => listReceivedMail(), retry: false, enabled: q.isSuccess });
  const [shown, setShown] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState<RereadReport | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState('');

  if (q.isError) return null;
  const data = q.data;

  async function generate(rotate: boolean) {
    if (rotate && !window.confirm('Make a new intake address? The old one stops working at once; replace the member in the support@ group.')) return;
    setBusy(true); setError(''); setCopied(false);
    try {
      const r = await generateIntakeAddress(rotate);
      setShown(r.address);
      await client.invalidateQueries({ queryKey: KEY });
    } catch (err) { setError(await getDisplayErrorMessage(err, 'The address was not made.')); } finally { setBusy(false); }
  }
  async function reread(apply: boolean) {
    setBusy(true); setError('');
    try {
      setReport(await rereadReceivedMail(apply));
      if (apply) await client.invalidateQueries({ queryKey: KEY });
    } catch (err) { setError(await getDisplayErrorMessage(err, 'Received mail could not be read.')); } finally { setBusy(false); }
  }
  async function save() {
    const patch: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(edits)) {
      if (k === 'human_tier_aliases' || k === 'auto_tier_aliases') patch[k] = v.split(',').map((x) => x.trim()).filter(Boolean);
      else patch[k] = Number(v);
    }
    setBusy(true); setError(''); setSaved('');
    try {
      await saveInboundMailSettings(patch);
      setEdits({}); setSaved('Saved.');
      await client.invalidateQueries({ queryKey: KEY });
    } catch (err) { setError(await getDisplayErrorMessage(err, 'The settings were not saved.')); } finally { setBusy(false); }
  }
  const value = (k: string): string => edits[k] ?? (Array.isArray(data?.settings[k]) ? (data?.settings[k] as string[]).join(', ') : String(data?.settings[k] ?? ''));

  return <section id="inbound-mail" className={styles.platformFeeSection}>
    <div className={styles.platformFeeHeader}>
      <div className={styles.headerIcon}><Inbox size={16} /></div>
      <div>
        <h2 className={styles.platformFeeTitle}>Inbound mail</h2>
        <p className={styles.platformFeeHint}>Every message to support@ becomes a Conversation once the intake address is a member of the support@ Google
          Group and receiving is on. Nothing is answered or refunded from here.</p>
      </div>
    </div>
    {q.isLoading || !data ? <p className={styles.platformFeeHint}><Loader2 size={13} className={styles.spin} /> Loading.</p> : <>
      <div className={styles.platformFeeField}>
        <span className={styles.platformFeeLabel}>Receiving</span>
        <p className={styles.platformFeeHint}>
          {data.receiving.enabled ? 'On' : 'Off (infra phase 2b switches it on)'}; topic {data.receiving.topic_configured ? 'set' : 'not set'}; bucket{' '}
          {data.receiving.bucket_configured ? 'set' : 'not set'}; domain {data.receiving.domain}; reply addresses on driver email{' '}
          {data.receiving.reply_alias_on ? 'on' : 'off (phase 3)'}.
        </p>
      </div>

      <div className={styles.platformFeeField}>
        <span className={styles.platformFeeLabel}>Intake address</span>
        {shown ? <>
          <p className={styles.platformFeeHint}>Shown once. Add it to support@ in Google Groups as a member, delivery Each email.</p>
          <div className={styles.platformFeeInputRow}>
            <input className={styles.platformFeeInput} readOnly value={shown} aria-label="Intake address" />
            <button type="button" className={styles.platformFeeBtn}
              onClick={() => { void navigator.clipboard?.writeText(shown).then(() => setCopied(true)).catch(() => undefined); }}>
              {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
            </button>
          </div>
        </> : data.intake.generated ? <>
          <p className={styles.platformFeeHint}>{data.intake.address_masked} (made {data.intake.generated_at ? new Date(data.intake.generated_at).toLocaleString() : 'earlier'})</p>
          <div className={styles.platformFeeInputRow}>
            <button type="button" className={styles.platformFeeBtn} disabled={busy} onClick={() => void generate(true)}>Make a new address</button>
          </div>
        </> : <div className={styles.platformFeeInputRow}>
          <button type="button" className={styles.platformFeeBtn} disabled={busy} onClick={() => void generate(false)}>Generate the intake address</button>
        </div>}
      </div>

      <div className={styles.platformFeeField}>
        <span className={styles.platformFeeLabel}>Capture checklist</span>
        <ul className={styles.platformFeeHint}>
          {data.checklist.map((s) => <li key={s.key}>{s.done ? 'Done' : 'To do'}: {s.label}{s.detail ? `. ${s.detail}` : ''}</li>)}
        </ul>
      </div>

      <div className={styles.platformFeeField}>
        <span className={styles.platformFeeLabel}>Received mail for a person</span>
        {received.isLoading ? <p className={styles.platformFeeHint}><Loader2 size={13} className={styles.spin} /> Loading.</p>
          : (received.data ?? []).length === 0 ? <p className={styles.platformFeeHint}>None. Quarantined mail and Google Groups invitations show here.</p>
            : <ul className={styles.platformFeeHint}>{(received.data ?? []).map((r) => <li key={r.id}>
              {new Date(r.received_at).toLocaleString()}: {r.outcome.replace(/_/g, ' ')}{r.reason ? ` (${r.reason.replace(/_/g, ' ')})` : ''}, from{' '}
              {r.sender.email ?? 'unknown'}, {r.subject || '(no subject)'}
              {r.preview && <details><summary>Read it</summary><pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{r.preview}</pre></details>}
            </li>)}</ul>}
      </div>

      <div className={styles.platformFeeField}>
        <span className={styles.platformFeeLabel}>Re-read received mail</span>
        <p className={styles.platformFeeHint}>Reads the copies SES kept (30 days) and runs any the platform never finished through the same path. Check first.
          {!data.receiving.enabled && ' Receiving is off, so Check lists what is waiting and nothing is read until it is on.'}</p>
        <div className={styles.platformFeeInputRow}>
          <button type="button" className={styles.platformFeeBtn} disabled={busy} onClick={() => void reread(false)}><RefreshCw size={13} /> Check</button>
          {/* The Api refuses apply while receiving is off (INBOUND_RECEIVING_OFF); the button follows the same switch. */}
          {report && !report.applied && report.missing.length > 0 && data.receiving.enabled && (
            <button type="button" className={styles.platformFeeBtn} disabled={busy} onClick={() => void reread(true)}>Re-read {report.missing.length}</button>
          )}
        </div>
        {report && <p className={styles.platformFeeHint} role="status">
          {report.listed} copies, {report.finished} already done, {report.missing.length} not finished
          {report.applied ? `; read: ${report.processed.map((p) => p.outcome.replace(/_/g, ' ')).join(', ') || 'none'}` : ''}{report.truncated ? '; more remain' : ''}.
        </p>}
      </div>

      <div className={styles.platformFeeField}>
        <span className={styles.platformFeeLabel}>Values</span>
        {NUMBER_FIELDS.map((f) => <div key={f.key} className={styles.platformFeeInputRow}>
          <label className={styles.platformFeeHint} htmlFor={`inbound-${f.key}`} style={{ flex: '1 1 220px' }}>
            {f.label}{f.key === 'stripe_reads_per_day' && data.stripe_reads_today !== undefined ? ` (used today: ${data.stripe_reads_today})` : ''}</label>
          <input id={`inbound-${f.key}`} className={styles.platformFeeInput} inputMode="decimal" value={value(f.key)} disabled={busy}
            onChange={(e) => { setEdits({ ...edits, [f.key]: e.target.value }); setSaved(''); }} style={{ maxWidth: 110 }} />
        </div>)}
        <div className={styles.platformFeeInputRow}>
          <label className={styles.platformFeeHint} htmlFor="inbound-human" style={{ flex: '1 1 220px' }}>Aliases always for a person (hosts and carbon always stay; support@ is the only alias EVII may draft for)</label>
          <input id="inbound-human" className={styles.platformFeeInput} value={value('human_tier_aliases')} disabled={busy}
            onChange={(e) => { setEdits({ ...edits, human_tier_aliases: e.target.value }); setSaved(''); }} />
        </div>
        <div className={styles.platformFeeInputRow}>
          <button type="button" className={styles.platformFeeBtn} disabled={busy || Object.keys(edits).length === 0} onClick={() => void save()}>Save</button>
        </div>
        {saved && <p className={styles.platformFeeHint} role="status">{saved}</p>}
      </div>
      {error && <p className={styles.platformFeeHint} role="alert" style={{ color: 'var(--color-err)' }}>{error}</p>}
    </>}
  </section>;
}
